/**
 * useBugReports - Hook para gerenciamento de bug reports e categorias
 */
import { useState, useCallback, useEffect } from 'react';
import {
  listarBugReports,
  listarCategorias,
  getStats,
  atualizarBugReport,
  detalheBugReport,
  BugReport,
  BugReportListResponse,
  BugReportFilters,
  BugReportStats,
  BugReportUpdateInput,
  CategoriaBugReport,
} from '../services/bugReportService';

export const useBugReports = () => {
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [categorias, setCategorias] = useState<CategoriaBugReport[]>([]);
  const [stats, setStats] = useState<BugReportStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchBugReports = useCallback(async (filters: BugReportFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res: BugReportListResponse = await listarBugReports({
        page_size: 20,
        ...filters,
      });
      setBugReports(res.items);
      setTotalPages(res.total_pages);
      setTotal(res.total);
      setCurrentPage(res.page);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Erro ao carregar ocorrencias');
      setBugReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const s = await getStats();
      setStats(s);
    } catch (err) {
      console.error('Erro ao carregar stats:', err);
    }
  }, []);

  const fetchCategorias = useCallback(async (apenasAtivas: boolean = false) => {
    try {
      const cats = await listarCategorias(apenasAtivas);
      setCategorias(cats);
      console.debug(
        `[useBugReports] Categorias carregadas: ${cats.length} ` +
        `(apenasAtivas=${apenasAtivas})`,
      );
    } catch (err: any) {
      // Defensividade: nunca deixa o dropdown silenciosamente vazio sem feedback.
      // Backend pode estar fora, token JWT pode ter expirado, etc.
      console.error('[useBugReports] Falha ao carregar categorias:', err);
      setCategorias([]); // garante array vazio (UI mostra empty state)
      // Toast nao bloqueante — se import.meta.env.MODE for production, pode
      // suprimir, mas em geral feedback eh util.
      try {
        const msg = err?.response?.data?.detail
          || err?.message
          || 'Erro ao carregar categorias de bug.';
        // Lazy import evita require ciclico em SSR/teste
        const { toast } = await import('react-hot-toast');
        toast.error(`Categorias: ${msg}. Verifique conexao ou login.`);
      } catch {
        // Se toast nao disponivel (ex: testes), falha silenciosa OK
      }
    }
  }, []);

  const updateBugReport = useCallback(async (id: number, data: BugReportUpdateInput) => {
    const updated = await atualizarBugReport(id, data);
    setBugReports((prev) => prev.map((br) => (br.id === id ? updated : br)));
    await fetchStats();
    return updated;
  }, [fetchStats]);

  const getDetalhe = useCallback(async (id: number) => {
    return await detalheBugReport(id);
  }, []);

  return {
    bugReports,
    categorias,
    stats,
    loading,
    error,
    totalPages,
    total,
    currentPage,
    fetchBugReports,
    fetchStats,
    fetchCategorias,
    updateBugReport,
    getDetalhe,
  };
};
